import React, { useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  StatusBar,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchLinkedAccounts, deleteLinkedAccount } from '@/store/slices/residents/homeSlice';
import { COLORS, PADDING } from '@/constants/residents/homeConstants';
import { connectionsStrings } from '@/constants/residents/strings';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { LinkedAccount } from '@/types/residents/home';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopLinkedAccountsView } from '@/screens/residents/linkedAccounts/desktop/DesktopLinkedAccountsView';
import { useToast } from '@/contexts/admin/ToastContext';

// NOTE: Mobile now uses ResidentLinkedAccountsScreen via ResidentMobileNavigator
// This component is kept for desktop web and tablet layouts only

/*
 * OLD MOBILE IMPORTS - No longer used, keeping for reference
 * Mobile now uses ResidentLinkedAccountsScreen with new Figma design
 * 
 * import { ConnectionsTabView } from '@/components/residents/connections/ConnectionsTabView';
 * import { InvitationsTab } from '@/components/residents/invitations/InvitationsTab';
 * import { LinkedAccountsList } from '@/components/residents/linkedAccounts/LinkedAccountsList';
 * import { MobileLinkedAccountsScreen } from '@/screens/residents/linkedAccounts/mobile/MobileLinkedAccountsScreen';
 */

type LinkedAccountsNavigationProp = StackNavigationProp<RootStackParamList>;

/**
 * LinkedAccountsScreen - Used for Desktop Web and Tablet layouts
 * 
 * NOTE: Mobile navigation now uses ResidentLinkedAccountsScreen via ResidentMobileNavigator
 * which includes the new Figma design with gradient frames, tabs accordion, etc.
 */
export const LinkedAccountsScreen: React.FC = () => {
  const navigation = useNavigation<LinkedAccountsNavigationProp>();
  const dispatch = useAppDispatch();
  const { linkedAccounts, isLoading } = useAppSelector((state) => state.home);
  const { layoutMode } = useResponsive();
  const { showSuccess, showError } = useToast();
  
  // Set navigation title to match iOS "Connections"
  useEffect(() => {
    navigation.setOptions({
      title: connectionsStrings.title,
    });
  }, [navigation]);

  // Load linked accounts when screen mounts
  useEffect(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  // Handle remove account
  const handleRemoveAccount = useCallback(async (linkedAccount: LinkedAccount) => {
    try {
      await dispatch(deleteLinkedAccount(linkedAccount.id)).unwrap();
      showSuccess('Recipient removed');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove linked account';
      showError(message);
    }
  }, [dispatch, showSuccess, showError]);

  // Handle send invite - navigate to NewInvitationScreen
  const handleSendInvite = useCallback(() => {
    navigation.navigate('NewInvitation');
  }, [navigation]);

  // Desktop web rendering
  if (layoutMode === 'desktop') {
    return (
      <DesktopLinkedAccountsView
        linkedAccounts={linkedAccounts}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onRemove={handleRemoveAccount}
        onSendInvite={handleSendInvite}
      />
    );
  }

  /*
   * OLD MOBILE/TABLET FALLBACK - Commented out
   * Mobile now uses ResidentLinkedAccountsScreen via ResidentMobileNavigator
   * 
   * // Mobile rendering with new Figma design
   * if (layoutMode === 'mobile') {
   *   return <MobileLinkedAccountsScreen />;
   * }
   *
   * // Fallback to old tab view for tablet or other layouts
   * return (
   *   <View style={styles.container}>
   *     <StatusBar barStyle="dark-content" />
   *     
   *     <ConnectionsTabView
   *       linkedAccounts={linkedAccounts}
   *       onRemoveLinkedAccount={handleRemoveAccount}
   *       onSendInvite={handleSendInvite}
   *       renderAccountsTab={renderAccountsTab}
   *       renderInvitationsTab={renderInvitationsTab}
   *     />
   *   </View>
   * );
   */

  // Tablet fallback - render desktop view for now
  // TODO: Create tablet-specific layout if needed
  return (
    <DesktopLinkedAccountsView
      linkedAccounts={linkedAccounts}
      isLoading={isLoading}
      onRefresh={handleRefresh}
      onRemove={handleRemoveAccount}
      onSendInvite={handleSendInvite}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  list: {
    flex: 1,
  },
  desktopList: {
    paddingHorizontal: PADDING.padding4,
  },
  listContent: {
    paddingTop: PADDING.padding4,
    paddingBottom: PADDING.padding6,
  },
  emptyListContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  desktopItemWrapper: {
    marginBottom: PADDING.padding4,
  },
});
